import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../redux/authSlice';
import {
  LayoutDashboard, Building2, Receipt, CreditCard, Key,
  Settings, LogOut, Mail, Bell, Menu,
  ChevronLeft, ChevronRight, Zap, Tag,
  ShieldCheck, Terminal, Search, User, HardDrive, Wallet, ArrowLeftRight
} from 'lucide-react';
import alertService from '../../utils/alert';
import { BrandLink } from '../../components/BrandLogo';
import { cn } from '../../utils/cn';
import Breadcrumb from '../../components/Breadcrumb';

export default function SuperAdminLayout() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const location  = useLocation();
  const { user, isSuperAdmin } = useSelector(state => state.auth);

  const [collapsed, setCollapsed]   = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuQuery, setMenuQuery]   = useState('');

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => { setMenuQuery(''); }, [collapsed]);

  const prefix = useMemo(() =>
    location.pathname.startsWith('/superadmin') ? '/superadmin' : '/admin',
  [location.pathname]);

  const NAV = useMemo(() => {
    const sections = [
      {
        label: 'System Control',
        items: [
          { to: `${prefix}/dashboard`, icon: LayoutDashboard, label: 'Overview' },
        ],
      },
      {
        label: 'Administration',
        items: [
          { to: `${prefix}/companies`, icon: Building2,  label: 'Companies'          },
          ...(isSuperAdmin && location.pathname.startsWith('/superadmin') ? [
            { to: '/superadmin/invoices', icon: Receipt, label: 'Billing Logs' },
            { to: '/superadmin/reconciliation', icon: ShieldCheck, label: 'Reconciliation' },
          ] : []),
          { to: `${prefix}/plans`,   icon: CreditCard, label: 'Subscription Plans' },
          { to: `${prefix}/api-credit-plans`, icon: Zap, label: 'API Credit Plans' },
          { to: `${prefix}/coupons`, icon: Tag,        label: 'Promo Coupons'      },
        ],
      },
    ];

    if (isSuperAdmin) {
      sections.push({
        label: 'Internal Settings',
        items: [
          { to: '/superadmin/settings',        icon: Settings, label: 'Core Settings'   },
          { to: '/superadmin/api-keys',        icon: Key,      label: 'Security Keys'   },
          { to: '/superadmin/platform-keys',   icon: ShieldCheck, label: 'Platform Integrations' },
          { to: '/superadmin/email-templates', icon: Mail,     label: 'Email Dispatch'  },
          { to: '/superadmin/profile',         icon: User,     label: 'My Profile'      },
        ],
      });
      sections.push({
        label: 'Diagnostics',
        items: [
          { to: '/superadmin/system-errors', icon: Terminal, label: 'System Health' },
          { to: '/superadmin/storage-health', icon: HardDrive, label: 'Storage Health' },
          { to: '/superadmin/payment-gateways', icon: Wallet, label: 'Payment Gateways' },
          { to: '/superadmin/payment-transactions', icon: ArrowLeftRight, label: 'SaaS Payment Transactions' },
        ],
      });
    }

    return sections;
  }, [prefix, isSuperAdmin, location.pathname]);

  const filteredNav = useMemo(() => {
    const q = menuQuery.trim().toLowerCase();
    if (!q) return NAV;
    return NAV.map(s => ({
      ...s,
      items: s.items.filter(item =>
        item.label.toLowerCase().includes(q) ||
        s.label.toLowerCase().includes(q)
      ),
    })).filter(s => s.items.length > 0);
  }, [NAV, menuQuery]);

  const handleLogout = useCallback(async () => {
    const confirmed = await alertService.confirm(
      'Confirm Logout',
      'Are you sure you want to terminate the current administrative session?',
      'warning',
      'Logout'
    );
    if (confirmed) {
      dispatch(logout());
      navigate('/login');
    }
  }, [dispatch, navigate]);

  const initials = (user?.name || user?.email || 'SA')
    .split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const displayName = user?.companyName || user?.name || user?.email || 'Admin';

  return (
    <div className="flex h-screen overflow-hidden bg-[#F3F3F3]">

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={cn(
 'fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-[#D8DDE6] transition-all duration-300 ease-in-out lg:static lg:translate-x-0 shadow-sm lg:shadow-none',
 collapsed ? 'w-16' : 'w-64',
 mobileOpen ? 'translate-x-0' : '-translate-x-full'
 )}>

        {/* Brand */}
        <div className="h-14 flex items-center px-4 border-b border-[#D8DDE6] bg-white overflow-hidden">
          {!collapsed ? (
            <BrandLink to="/" size={36} subtitle="Super Admin" className="max-w-full" wordmarkClassName="pr-2" />
          ) : (
            <BrandLink to="/" size={34} showText={false} />
          )}
        </div>

        {/* Menu search */}
        {!collapsed && (
          <div className="px-3 py-3 border-b border-[#EEF1F6] bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F4F6F9] border border-[#D8DDE6] rounded focus-within:border-[#0176D3] focus-within:ring-1 focus-within:ring-[#0176D3]/20 transition-all">
              <Search size={14} className="text-[#706E6B]" />
              <input
                type="text"
                value={menuQuery}
                onChange={e => setMenuQuery(e.target.value)}
                placeholder="Filter menu…"
                className="w-full bg-transparent border-none outline-none text-[12px] text-[#080707] placeholder:text-[#706E6B] font-medium"
              />
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-1 no-scrollbar bg-white">
          {filteredNav.map((section, sIdx) => (
            <div key={sIdx} className="space-y-1">
              {!collapsed && (
                <div className="px-4 py-2">
                  <span className="text-[11px] font-semibold text-[#706E6B]">
                    {section.label}
                  </span>
                </div>
              )}
              <div className="space-y-0.5">
                {section.items.map(item => {
                  const isActive = location.pathname === item.to ||
                    location.pathname.startsWith(item.to + '/');
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={cn(
 'flex items-center gap-3 px-3 py-2 transition-colors text-[13px] rounded',
 isActive
 ? 'bg-[#EEF4FF] text-[#0176D3] font-semibold'
 : 'text-[#2E2E2E] hover:bg-[#F3F2F2]'
 )}
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon size={16} className="shrink-0" />
                      {!collapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Profile / Logout */}
        <div className="p-4 border-t border-[#D8DDE6] bg-white">
          <div className={cn('flex items-center gap-3', collapsed && 'justify-center')}>
            <div className="w-8 h-8 rounded bg-[#0176D3] text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-sm">
              {initials}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0 pr-2">
                <p className="text-[13px] font-bold text-[#080707] truncate">
                  {displayName}
                </p>
                <button
                  onClick={handleLogout}
                  className="text-[13px] font-semibold text-[#706E6B] hover:text-[#BA0517] hover:bg-[#FEF1EE]/50 p-1 px-1.5 rounded transition-all"
                >
                  Log Out
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0 bg-[#F3F3F3] text-[#080707]">

        {/* Header */}
        <header className="h-14 flex items-center justify-between gap-2 px-3 sm:px-6 bg-white border-b border-[#D8DDE6] shrink-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden p-2 text-[#706E6B] hover:bg-[#F3F2F2] rounded transition-colors"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={20} />
            </button>
            <button
              className="hidden lg:flex items-center justify-center p-2 text-[#706E6B] hover:bg-[#F3F2F2] hover:text-[#0176D3] rounded transition-colors border border-transparent hover:border-[#D8DDE6]"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
            <Breadcrumb />
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button className="p-2 text-[#706E6B] hover:text-[#0176D3] hover:bg-[#F3F2F2] rounded transition-all">
                <Bell size={18} />
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-[#F3F3F3] relative">
          <div className="w-full h-full p-4 md:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
